import { ISiteTasksService, SiteTask, CreateSiteTaskRequest, UpdateSiteTaskRequest, TaskCompletion, CompleteTaskRequest } from './types';
import { siteTasksApiService } from './apiService';
import { siteTasksMockService } from './mockService';

/**
 * Service for site task management
 * Attempts to use the API service first, falls back to mock service if API fails
 */
export class SiteTasksService implements ISiteTasksService {
  private apiService = siteTasksApiService;
  private mockService = siteTasksMockService;

  /**
   * Get all tasks for a site
   */
  async getSiteTasks(siteId: string): Promise<SiteTask[]> {
    console.log('SiteTasksService.getSiteTasks called with siteId:', siteId);
    try {
      // Try to make a real API call first
      return await this.apiService.getSiteTasks(siteId);
    } catch (error) {
      console.log('Using mock site tasks data due to API error:', error);
      // Fallback to mock data
      return this.mockService.getSiteTasks(siteId);
    }
  }

  /**
   * Get site task by ID
   */
  async getSiteTaskById(siteId: string, taskId: string): Promise<SiteTask> {
    try {
      // Try to make a real API call first
      return await this.apiService.getSiteTaskById(siteId, taskId);
    } catch (error) {
      console.log('Using mock site task data due to API error:', error);
      // Fallback to mock data
      return this.mockService.getSiteTaskById(siteId, taskId);
    }
  }

  /**
   * Create a new site task
   */
  async createSiteTask(siteId: string, taskData: CreateSiteTaskRequest): Promise<SiteTask> {
    console.log('SiteTasksService.createSiteTask called with siteId:', siteId, 'and taskData:', taskData);
    try {
      // Try to make a real API call first
      return await this.apiService.createSiteTask(siteId, taskData);
    } catch (error) {
      console.log('Using mock site task creation due to API error:', error);
      // Fallback to mock creation
      return this.mockService.createSiteTask(siteId, taskData);
    }
  }

  /**
   * Update a site task
   */
  async updateSiteTask(siteId: string, taskId: string, taskData: UpdateSiteTaskRequest): Promise<boolean> {
    try {
      // Try to make a real API call first
      return await this.apiService.updateSiteTask(siteId, taskId, taskData);
    } catch (error) {
      console.log('Using mock site task update due to API error:', error);
      // Fallback to mock update
      return this.mockService.updateSiteTask(siteId, taskId, taskData);
    }
  }

  /**
   * Delete a site task
   */
  async deleteSiteTask(siteId: string, taskId: string): Promise<boolean> {
    try {
      // Try to make a real API call first
      return await this.apiService.deleteSiteTask(siteId, taskId);
    } catch (error) {
      console.log('Using mock site task deletion due to API error:', error);
      // Fallback to mock deletion
      return this.mockService.deleteSiteTask(siteId, taskId);
    }
  }

  /**
   * Complete a site task
   */
  async completeTask(siteId: string, taskId: string, completionData: CompleteTaskRequest): Promise<TaskCompletion> {
    try {
      // Try to make a real API call first
      return await this.apiService.completeTask(siteId, taskId, completionData);
    } catch (error) {
      console.log('Using mock task completion due to API error:', error);
      // Fallback to mock completion
      return this.mockService.completeTask(taskId, completionData);
    }
  }

  /**
   * Get all completions for all tasks at a site in a single request
   */
  async getAllSiteCompletions(siteId: string): Promise<Record<string, TaskCompletion[]>> {
    try {
      return await this.apiService.getAllSiteCompletions(siteId);
    } catch (error) {
      console.log('getAllSiteCompletions API failed, falling back to empty:', error);
      return {};
    }
  }

  /**
   * Get all completions for a task
   */
  async getTaskCompletions(siteId: string, taskId: string): Promise<TaskCompletion[]> {
    try {
      // Try to make a real API call first
      return await this.apiService.getTaskCompletions(siteId, taskId);
    } catch (error) {
      console.log('Using mock task completions data due to API error:', error);
      // Fallback to mock data
      return this.mockService.getTaskCompletions(taskId);
    }
  }

  /**
   * Reset completions for a specific task
   */
  async resetTaskCompletions(siteId: string, taskId: string, userId?: string): Promise<boolean> {
    try {
      // Try to make a real API call first
      return await this.apiService.resetTaskCompletions(siteId, taskId);
    } catch (error) {
      console.log('Using mock task completion reset due to API error:', error);
      // Fallback to mock reset
      return this.mockService.resetTaskCompletions(taskId, userId);
    }
  }
  
  /**
   * Reset all completions for a site
   */
  async resetAllTaskCompletions(siteId: string, userId?: string): Promise<boolean> {
    try {
      // Try to make a real API call first
      return await this.apiService.resetAllTaskCompletions(siteId);
    } catch (error) {
      console.log('Using mock site task completions reset due to API error:', error);
      // Fallback to mock reset
      return this.mockService.resetAllTaskCompletions(siteId, userId);
    }
  }
  
  /**
   * Clear all task completions from localStorage
   * This is useful when the database is cleared and we want to ensure
   * localStorage is in sync with the database
   */
  async clearAllTaskCompletions(): Promise<boolean> {
    try {
      // Try to make a real API call first (though this is likely not implemented in the API)
      return await this.apiService.clearAllTaskCompletions();
    } catch (error) {
      console.log('Using mock task completions clear due to API error:', error);
      // Fallback to mock clear
      return this.mockService.clearAllTaskCompletions();
    }
  }
}

// Create a default instance
export const siteTasksService = new SiteTasksService();

export default siteTasksService;
